/**
 * The governance change log, finally readable (redesign phase 3, item A3).
 *
 * ── What was wrong ──────────────────────────────────────────────────────
 *
 * `budget_audit` has recorded every cap, price, currency, model and credit
 * change since migration 0123 — append-only, trigger-enforced, written inside
 * the same transaction as the mutation it describes, with 25 distinct action
 * types. **Nothing read it.** In a product whose subject is spending
 * governance, "who set this customer's cap to zero" was a `psql` question.
 *
 * ── Why the DIFF is the feature, not the list ───────────────────────────
 *
 * A list of "someone changed tenant_caps" answers nothing. The value is in
 * old → new, and it is already stored: `writeBudgetAudit` deliberately
 * normalizes an absent value to an explicit `null` so that "cleared to
 * unlimited" stays distinguishable from "this field was not part of the
 * action". Rendering both as a dash would throw away the one distinction the
 * writer went out of its way to preserve — so `null` renders as the WORD, and
 * absence renders as nothing.
 */
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { describeAuditEntry, formatMoney, formatCurrencyMinor, billingMicroToCurrencyMinor, PLATFORM_DATE_LOCALE } from '@papan/types'
import { budgetApi, type AuditEntry, type AuditPage, type AuditQuery } from '../../client/budget'
import { Section, formatBytes } from './bits'

const PAGE_SIZE = 25

export function AuditLog({
  /** Omit for the PLATFORM-scope log (catalog, pricing, admin grants). */
  tenantId,
  title,
  desc,
  billingCurrency,
}: {
  tenantId?: string
  title: string
  desc: string
  /**
   * The deployment's BILLING currency, for `manual_payment` rows alone — their
   * amount is minor units of it, not the USD microdollars every other row uses.
   *
   * Optional, and omitting it is safe rather than merely degraded: without it
   * `describeAuditEntry` gets no `currencyMinor` formatter, so a payment row
   * falls back to the raw diff instead of rendering MYR 1,200.00 with a dollar
   * sign. Never substitute `displayCurrency` here — that is a render-time
   * multiplier for spend, a different thing entirely, and conflating the two is
   * the mistake this codebase documents at length.
   */
  billingCurrency?: string
}) {
  const { t } = useTranslation('core-ui')
  const [page, setPage] = useState<AuditPage | null>(null)
  const [offset, setOffset] = useState(0)
  const [actor, setActor] = useState('')
  const [actorInput, setActorInput] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const id = setTimeout(() => { setActor(actorInput.trim()); setOffset(0) }, 300)
    return () => clearTimeout(id)
  }, [actorInput])

  const load = useCallback(async (nextOffset: number) => {
    setLoading(true)
    try {
      const q: AuditQuery = { limit: PAGE_SIZE, offset: nextOffset, actor: actor || undefined }
      setPage(tenantId
        ? await budgetApi.getTenantAudit(tenantId, q)
        : await budgetApi.getPlatformAudit(q))
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to load activity')
    } finally {
      setLoading(false)
    }
  }, [tenantId, actor])

  useEffect(() => { load(offset) }, [load, offset])

  if (!page) {
    if (loading) return <div className="p-4 themed-text-muted text-sm">{t('common:loading')}</div>
    return <div className="p-4 text-sm text-red-400">{error}</div>
  }

  const to = Math.min(page.offset + page.limit, page.total)

  return (
    <Section title={title} desc={desc}>
      <div className="mb-3">
        <input
          value={actorInput}
          onChange={(e) => setActorInput(e.target.value)}
          placeholder={t('settings.platform.audit.actorPh', { defaultValue: 'Filter by who made the change' })}
          aria-label={t('settings.platform.audit.actorPh', { defaultValue: 'Filter by who made the change' })}
          className="themed-input border rounded px-2 py-1 text-xs w-72 outline-none focus:border-blue-500"
        />
      </div>

      <div
        className={`themed-bg-secondary rounded-lg overflow-hidden transition-opacity ${loading ? 'opacity-60' : ''}`}
        aria-busy={loading}
      >
        {page.entries.length === 0 && (
          <div className="px-3 py-4 themed-text-muted text-sm">
            {t('settings.platform.audit.empty', { defaultValue: 'No recorded changes.' })}
          </div>
        )}
        {page.entries.map((e) => <AuditRow key={e.id} entry={e} billingCurrency={billingCurrency} />)}
      </div>

      {page.total > PAGE_SIZE && (
        <div className="flex items-center gap-3 mt-3">
          <button
            disabled={page.offset === 0}
            onClick={() => setOffset(Math.max(0, page.offset - PAGE_SIZE))}
            className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-40"
          >
            {t('settings.budget.prev')}
          </button>
          <span className="text-xs themed-text-muted">
            {t('settings.budget.pageRange', {
              from: page.total === 0 ? 0 : page.offset + 1, to, total: page.total,
            })}
          </span>
          <button
            disabled={to >= page.total}
            onClick={() => setOffset(page.offset + PAGE_SIZE)}
            className="px-3 py-1.5 text-xs rounded themed-btn-secondary disabled:opacity-40"
          >
            {t('settings.budget.next')}
          </button>
        </div>
      )}
    </Section>
  )
}

function AuditRow({ entry, billingCurrency }: { entry: AuditEntry; billingCurrency?: string }) {
  const { t } = useTranslation('core-ui')
  const when = new Date(entry.createdAt)

  // Plain English, keyed on `action` (NOT `subject` — see audit-copy.ts).
  // `raw` is the signal that no formatter matched, in which case this renders
  // the JSON diff exactly as the screen always did.
  //
  // The formatters are injected because the units differ per action and the
  // choice belongs at the call site: `money` is USD microdollars (the ledger's
  // unit), `currencyMinor` is minor units of the BILLING currency (used by
  // `manual_payment` alone), and `currencyMicro` is BILLING-currency MICRO
  // units — 1e-6 of the major unit, `tenant_extra_settlements`' own unit —
  // used by `extra_settlement` alone. Reusing `currencyMinor` there would be
  // off by 10,000x on a 2-decimal currency (see `AuditFormatters.currencyMicro`'s
  // own doc comment).
  const { title, sentence, raw, subjectIsInternal } = describeAuditEntry(entry, {
    money: (micro) => formatMoney(micro),
    bytes: formatBytes,
    // Deliberately absent when the caller did not supply a currency — see the
    // prop's own note. An absent formatter degrades that one action to the raw
    // diff; a guessed currency would print a confident wrong number.
    currencyMinor: billingCurrency
      ? (minor: number) => formatCurrencyMinor(minor, billingCurrency)
      : undefined,
    // `billingMicroToCurrencyMinor` THROWS on a currency it cannot resolve an
    // exponent for (unlike `formatCurrencyMinor`, which degrades to a labeled
    // string) — a guard the ledger boundary wants and a render path does not.
    // `billingCurrency` is server-validated in the ordinary case, but this
    // still must not be the one money formatter on the page that can crash
    // the row instead of just showing the raw diff.
    currencyMicro: billingCurrency
      ? (micro: number) => {
          try {
            return formatCurrencyMinor(billingMicroToCurrencyMinor(micro, billingCurrency), billingCurrency)
          } catch {
            return `${micro}µ ${billingCurrency.toUpperCase()}`
          }
        }
      : undefined,
  })

  return (
    <div className="px-3 py-2.5 border-b themed-border last:border-b-0">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <span className="themed-text text-sm">{title}</span>
          {/* The subject stays, but only when it is a TARGET the reader needs
              and the sentence has not already absorbed it. Two suppressions,
              and they are different: `subjectIsInternal` means the subject is a
              variant selector the title already spells out ("Who decides
              plans" + `plan_authority`), while the `includes` test catches the
              same fact twice ("For demo-member@papan.local: $40.00" beside a
              monospace `demo-member@papan.local`). */}
          {entry.subject && !subjectIsInternal && !sentence?.includes(entry.subject) && (
            <span className="themed-text-muted text-xs font-mono ml-2">{entry.subject}</span>
          )}
        </div>
        <div className="text-[11px] themed-text-muted shrink-0">
          {/* The actor is captured BY VALUE at write time — the membership row
              may be long gone, which is exactly when you need to know who. */}
          {entry.actorEmail ?? t('settings.platform.audit.system', { defaultValue: 'system' })}
          {' · '}
          <time dateTime={entry.createdAt} title={when.toISOString()}>
            {when.toLocaleString(PLATFORM_DATE_LOCALE)}
          </time>
        </div>
      </div>

      {!raw && sentence
        ? (
          <>
            <div className="mt-0.5 themed-text-secondary text-xs">{sentence}</div>
            {/* The raw diff survives, one click away. An operator reconciling
                this against the database needs the exact stored value, and a
                sentence is a lossy rendering of it by design. */}
            <details className="mt-1 group">
              <summary className="text-[11px] themed-text-muted cursor-pointer list-none hover:themed-text transition-colors">
                <span aria-hidden className="inline-block w-3 transition-transform group-open:rotate-90">›</span>
                {t('settings.platform.audit.showRaw', { defaultValue: 'Show what was stored' })}
              </summary>
              <Diff oldValue={entry.oldValue} newValue={entry.newValue} exact />
            </details>
          </>
        )
        // No formatter for this action — render exactly what the screen did
        // before, rather than nothing.
        : <Diff oldValue={entry.oldValue} newValue={entry.newValue} />}
    </div>
  )
}

/** A plain `{}` object (not an array, not null) — the shape worth flattening. */
function plainObj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null
}

/** `lowBalanceThresholdBillingMicro` → "Low balance threshold billing micro".
 *  A raw stored key is still better read as words than as a camelCase blob. */
function prettyKey(k: string): string {
  const s = k
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .toLowerCase()
  return s.charAt(0).toUpperCase() + s.slice(1)
}

/**
 * old → new.
 *
 * Two callers, and the `exact` flag is the difference:
 *  - The "Show what was stored" details panel (`exact`) — for reconciling a row
 *    against the database, so it renders the WHOLE value verbatim, every field,
 *    exactly as stored. Never flattened.
 *  - The raw FALLBACK, reached when `describeAuditEntry` has no sentence (rule 5)
 *    — the tenant-feed long tail and any action added after audit-copy.ts. It
 *    used to render `JSON.stringify(value)`, so an operator saw
 *    `{"currency":"myr","rate":"5.500000"}`. When BOTH sides are objects (or an
 *    absent null/undefined), it now lists every field one per line so the row
 *    reads as words — and lists ALL fields, not only the changed ones, because
 *    this fallback view has no `exact` sibling and hiding an unchanged field
 *    would make it visible nowhere.
 *
 * A real scalar on either side (primitive vs object) is NOT flattened — the
 * object's keys would render the scalar side as "—" and lose it — so that case
 * falls to the plain value path below.
 *
 * `undefined` means the field was not part of this action and renders as
 * nothing; `null` means it was explicitly SET to null — for a money cap that is
 * "unlimited", which is the opposite of `0` and worth its own word rather than
 * a dash that could be read either way.
 */
function Diff({ oldValue, newValue, exact }: { oldValue: unknown; newValue: unknown; exact?: boolean }) {
  const { t } = useTranslation('core-ui')
  if (oldValue === undefined && newValue === undefined) return null

  // Fallback only, and only when neither side is a real scalar (a null/undefined
  // side is absence, which the per-key "—" renders correctly).
  const oldObj = plainObj(oldValue)
  const newObj = plainObj(newValue)
  const bothObjectish = (oldObj || oldValue == null) && (newObj || newValue == null) && (oldObj || newObj)
  if (!exact && bothObjectish) {
    const keys = Array.from(new Set([...Object.keys(oldObj ?? {}), ...Object.keys(newObj ?? {})]))
    if (keys.length > 0) {
      return (
        <div className="mt-1 flex flex-col gap-0.5 text-[11px] font-mono">
          {keys.map((k) => (
            <div key={k} className="flex items-center gap-2 flex-wrap">
              <span className="themed-text-muted not-italic">{prettyKey(k)}</span>
              <Value v={oldObj ? oldObj[k] : undefined} muted />
              <span className="themed-text-muted" aria-label={t('settings.platform.audit.changedTo', { defaultValue: 'changed to' })}>→</span>
              <Value v={newObj ? newObj[k] : undefined} />
            </div>
          ))}
        </div>
      )
    }
  }

  return (
    <div className="mt-1 flex items-center gap-2 text-[11px] font-mono flex-wrap">
      <Value v={oldValue} muted exact={exact} />
      <span className="themed-text-muted" aria-label={t('settings.platform.audit.changedTo', { defaultValue: 'changed to' })}>→</span>
      <Value v={newValue} exact={exact} />
    </div>
  )
}

function Value({ v, muted, exact }: { v: unknown; muted?: boolean; exact?: boolean }) {
  const { t } = useTranslation('core-ui')
  if (v === undefined) {
    return <span className="themed-text-muted italic">—</span>
  }
  if (v === null) {
    return (
      <span className={`italic ${muted ? 'themed-text-muted' : 'themed-text-secondary'}`}>
        {t('settings.platform.audit.null', { defaultValue: 'none' })}
      </span>
    )
  }
  // An array (e.g. model_restriction's model-id list) reads as a comma list, not
  // as `["a","b"]` — but ONLY in the pretty views. The `exact` reconciliation
  // panel must show it verbatim (it falls through to JSON.stringify below), or a
  // value with a comma in it becomes ambiguous against the database. An empty
  // array is the same "none" the null branch uses.
  if (Array.isArray(v) && !exact) {
    if (v.length === 0) {
      return (
        <span className={`italic ${muted ? 'themed-text-muted' : 'themed-text-secondary'}`}>
          {t('settings.platform.audit.null', { defaultValue: 'none' })}
        </span>
      )
    }
    const text = v.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')
    return (
      <span className={`${muted ? 'themed-text-muted line-through' : 'themed-text'} break-all`} title={text}>
        {text.length > 120 ? `${text.slice(0, 120)}…` : text}
      </span>
    )
  }
  const text = typeof v === 'object' ? JSON.stringify(v) : String(v)
  return (
    <span
      className={`${muted ? 'themed-text-muted line-through' : 'themed-text'} break-all`}
      title={text}
    >
      {text.length > 120 ? `${text.slice(0, 120)}…` : text}
    </span>
  )
}
